import { useForm } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { ItemChecklistServicoStatistic } from "../../../../domain/models/item_checklist_servico_statistic";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import ProgressBar from "../../../../../../common/components/progress_bar";

/**
 * CustomModalLoading
 */

const CustomModalLoading = ({
  loading,
  message,
}: { loading: boolean, message?: string }) => {
  return (
    <>
      {
        loading &&
        <div className="absolute size-full z-50 top-0 left-0 flex justify-center items-center">
          <div className="flex-col w-48 p-4 rounded-lg gap-2 border border-stone-500 flex justify-center items-center bg-[#FFFFFF] dark:bg-[#393939]">
            <div className="w-24">
              <img src="/images/mini-logo.png" alt="logo gdol" />
            </div>
            <div className="w-full">
              <ProgressBar />
              <p className="text-black dark:text-white text-center">{message ?? "Salvando"}</p>
            </div>
          </div>
        </div>
      }
    </>
  );
}

/**
 * ItemChecklistServicoModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedItemChecklistServico: ItemChecklistServicoStatistic | null,
  onClose: (openState: boolean) => void;
};

export const ItemChecklistServicoModal = ({
  open,
  saving,
  loading,
  selectedItemChecklistServico,
  onClose,
}: Props) => {
  const hookForm = useForm<any>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    if (open) {
      if (selectedItemChecklistServico) {
        hookForm.setValue('ordem', selectedItemChecklistServico.ordem ?? '');
        hookForm.setValue('descricao', selectedItemChecklistServico.descricao ?? '');
        hookForm.setValue('resumoItem', selectedItemChecklistServico.resumoItem ?? '');
        hookForm.setValue('delete', selectedItemChecklistServico.delete === true ? 'Sim' : 'Não');
      } else {
        hookForm.reset();
      }
    }
  }, [
    open,
    selectedItemChecklistServico,
    hookForm,
  ]);

  const handleClose = useCallback(() => {
    if (!saving && !loading) onClose(false);
  }, [
    saving,
    loading,
    onClose
  ]);

  return (
    <Dialog
      open={open}
      sx={{
        "& .MuiDialog-container": {
          position: 'relative',

          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "60rem",
          },
          "& .MuiFormControlLabel-label": {
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '13px',
          },
          "& .MuiCheckbox-root": {
            padding: '.25rem',
          },
        },
      }}>
      <DialogTitle>
        Item de checklist de serviço
      </DialogTitle>

      <DialogContent>
        <div className="w-full flex flex-col">
          <FormContainer title='Informações' arrangement='fullRow' gap='small' border={false}>
            <>
              <DefaultFormInput
                name='ordem'
                label='Ordem'
                readOnly={true}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='descricao'
                label='Descrição do Item'
                readOnly={true}
                multiline={true}
                rows={12}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='resumoItem'
                label='Resumo do Item'
                readOnly={true}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='delete'
                label='Excluido'
                readOnly={true}
                reactHookForm={hookForm}
              />

              {
                selectedItemChecklistServico &&
                <div className="w-full flex flex-row gap-2">
                  <div className="w-full flex flex-col flex-1 bg-[#26C281] px-4 py-2 rounded">
                    <span style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Aprovados</span>
                    <span style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }} className="text-2xl">{selectedItemChecklistServico.aprovados}</span>
                  </div>
                  <div className="w-full flex flex-col flex-1 bg-[#EF4836] px-4 py-2 rounded">
                    <span style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Reprovados</span>
                    <span style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }} className="text-2xl">{selectedItemChecklistServico.reprovados}</span>
                  </div>
                  <div className="w-full flex flex-col flex-1 bg-[#FFB848] px-4 py-2 rounded">
                    <span style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Retrabalhos</span>
                    <span style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }} className="text-2xl">{selectedItemChecklistServico.retrabalhos}</span>
                  </div>
                  <div className="w-full flex flex-col flex-1 bg-[#ffff00] px-4 py-2 rounded">
                    <span style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Não Se Aplica</span>
                    <span style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }} className="text-2xl">{selectedItemChecklistServico.naoSeAplica}</span>
                  </div>
                </div>
              }
            </>
          </FormContainer>
        </div>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div className="flex flex-row">
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Fechar
              </h1>
            </div>
          </div>
        </div>
      </DialogActions>

      <CustomModalLoading
        loading={saving || loading}
        message={'Carregando...'}
      />

    </Dialog>
  );
}